import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";

/** Mirrors BrainChanger.Domain.Models.AppUpdateModels.AppUpdateAnnouncementResponse
 * (FLIP-81). Field names are camelCase, matching this API's JSON convention - see any
 * generated ResponseModel class in api.client.generated.ts for the same shape. */
export interface AppUpdateAnnouncementResponse {
  version: string;
  title: string;
  newFeatures: string[];
  bugFixes: string[];
  isRequired: boolean;
  communityPostId?: number;
  communityPostUrl?: string;
}

interface ResultStatus {
  statusCode: number;
  errorText?: string;
}

export interface ResponseModel<T> {
  resultStatus: ResultStatus;
  responseData: T;
}

/**
 * FLIP-81. Hand-written rather than generated into api.client.generated.ts (never edited
 * by hand - see `npm run nswag`): regenerating that file needs the API running locally and
 * reachable at nswag.json's configured URL, which isn't safe to do casually since
 * BrainChanger.API applies pending migrations to whatever connection string is configured
 * on every startup, and that's a shared database today rather than a disposable local one.
 * Swap this out for a real generated AppUpdatesService once someone next regenerates it
 * against a genuinely local API instance.
 */
@Injectable({ providedIn: "root" })
export class AppUpdatesApiService {
  constructor(private http: HttpClient) {}

  /** responseData is null when there's nothing new for this user - the caller should treat
   * that as a clean no-op, not an error. */
  getLatest(): Observable<ResponseModel<AppUpdateAnnouncementResponse | null>> {
    return this.http.get<ResponseModel<AppUpdateAnnouncementResponse | null>>(
      `${environment.apiBaseUrl}/api/app-updates/latest`
    );
  }

  /** Idempotent and monotonic server-side - safe to call every time the modal is shown. */
  markSeen(version: string): Observable<ResponseModel<boolean>> {
    return this.http.post<ResponseModel<boolean>>(
      `${environment.apiBaseUrl}/api/app-updates/mark-seen`,
      { version }
    );
  }

  /** Anonymous - no auth token needed, and the version-poll that calls this runs before
   * login. Whether the given version's live announcement is required (1B) vs optional
   * (1A); false when no live announcement matches it. */
  isRequired(version: string): Observable<ResponseModel<boolean>> {
    return this.http.get<ResponseModel<boolean>>(
      `${environment.apiBaseUrl}/api/app-updates/is-required`,
      { params: { version } }
    );
  }
}
